import axios from 'axios'
import { XsibApiError } from './client'

/** i18next `t` funksiyasining bu yerda kerak bo'lgan qismi */
export type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * `venue` servisi endi HAR DOIM envelope qaytaradi
 * (`uz.xsib.common.response.XsibResponse`), xatoda `error` to'ldirilgan:
 * `{"success":false,"error":{"code":422,"tag":"CHALLENGE_EXPIRED","text":"..."}}`.
 * Kod tanlash jadvali `VenueExceptionHandler` da: 404/409/422/429/503.
 *
 * ⚠️ `error.text` matni HAR DOIM O'ZBEKCHA — server tomonidagi
 * `VenueException` xabarlari qattiq yozilgan
 * (`venue/api/VenueException.java`), ular ko'ruvchining tanlagan tiliga
 * (uz/ru/en) moslashmaydi. Shuning uchun bu ilova `text` ni
 * TO'G'RIDAN-TO'G'RI ko'rsatMAYDI — aks holda ruscha yoki inglizcha
 * interfeysda birdan o'zbekcha jumla chiqib qolardi.
 *
 * ⚠️ Ilgari bu farqlash `ProblemDetail` ning qo'shimcha maydonlari
 * (`reason`, `allowedMethods`, `required`/`actual`) orqali qilinardi.
 * `ErrorData` da uch maydon bor, shuning uchun ular endi BITTA
 * `tag` ga jamlangan. Bu aslida yaxshilanish: ilgari "backend kalit
 * qaytarmaydi" deb yozilgan chegara aynan shu bilan yopildi — `tag`
 * o'sha kalitning o'zi.
 */
interface ApiErrorBody {
  success?: boolean
  error?: { code?: number; tag?: string; text?: string } | null
}

export interface PortalError {
  message: string
  /** 429 holatida — tugmani shuncha vaqtga o'chirish uchun */
  retryAfterSeconds?: number
}

/**
 * 422 ning barcha sabablari — `tag` bo'yicha.
 *
 * ⚠️ Nomlar backend bilan kelishilgan shartnoma
 * (`VenueExceptionHandler`). Ular o'zgarsa bu yer JIMGINA umumiy
 * xabarga tushadi, shuning uchun ikkala tomonda ham bir vaqtda
 * o'zgartirilishi shart.
 */
const TAG_MESSAGE_KEY: Record<string, string> = {
  CHALLENGE_NOT_FOUND: 'error.challengeExpired',
  CHALLENGE_EXPIRED: 'error.challengeExpired',
  CHALLENGE_ALREADY_USED: 'error.challengeUsed',
  CHALLENGE_WRONG_CODE: 'error.challengeWrong',
  CHALLENGE_TOO_MANY_ATTEMPTS: 'error.challengeTooManyAttempts',
  METHOD_NOT_ALLOWED: 'error.methodNotAllowed',
  INSUFFICIENT_IDENTIFICATION: 'error.insufficientIdentification',
}

/**
 * Vaucher rad etilishi — **409**, 422 EMAS (`VenueExceptionHandler.voucherRejected`).
 *
 * ⚠️ Bu jadval yo'q edi va oqibati aniq ko'rinardi: karta rad etilganda
 * mehmon "Bu tarmoq hozir yangi mehmon qabul qilmayapti" degan xabarni
 * o'qirdi — chunki 409 ning YAGONA tarjimasi `NotAcceptingGuests` uchun
 * yozilgan edi. Kartasi ishlatilgan mehmon esa tarmoqni ayblab, xodimga
 * "Wi-Fi buzuq" deb borardi.
 *
 * Backend uchta holatni ATAYLAB ajratadi ("ishlatilgan", "muddati
 * o'tgan", "bekor qilingan" — uchta boshqacha keyingi qadam), shuning
 * uchun portal ham ularni aralashtirmaydi.
 *
 * ⚠️ `VOUCHER_UNUSABLE` — 2026-08-16 da qo'shildi. Karta TOPILDI, lekin
 * unga bog'langan paket sotuvga yaroqsiz (masalan arxivlangan). Ilgari
 * backend bu holatga `VOUCHER_NOT_FOUND` tegini qo'yardi va mehmon
 * "Kartadagi kodni tekshirib qayta kiriting" degan FOYDASIZ maslahatni
 * o'qirdi — kod to'g'ri terilgan edi, qayta terish hech narsani
 * o'zgartirmasdi. Bu yerga qo'shilmasa, teg jadvaldan tushib qolib
 * yuqoridagi izohda tasvirlangan aynan o'sha nuqson qaytardi
 * ("Bu tarmoq hozir yangi mehmon qabul qilmayapti").
 */
const VOUCHER_TAG_MESSAGE_KEY: Record<string, string> = {
  VOUCHER_ACTIVATED: 'error.voucherUsed',
  VOUCHER_EXPIRED: 'error.voucherExpired',
  VOUCHER_VOIDED: 'error.voucherVoided',
  VOUCHER_NOT_FOUND: 'error.voucherNotFound',
  VOUCHER_UNUSABLE: 'error.voucherUnusable',
  VOUCHER_LOCKED: 'error.cardLocked',
}

/**
 * 429 da kutish muddati endi standart `Retry-After` sarlavhasida
 * ({@code VenueExceptionHandler}) — tanadagi maxsus maydonda emas.
 *
 * ⚠️ Sarlavha yo'q yoki buzuq bo'lsa 60 sekundga tushamiz: tugmani
 * umuman bloklamaslik mehmonni darhol qayta urinishga va yana 429
 * olishga olib borardi.
 */
function retryAfterSeconds(headers: unknown): number | undefined {
  const raw = (headers as Record<string, unknown> | undefined)?.['retry-after']
  const seconds = Number(raw)
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined
}

/**
 * Kod + `tag` juftligini tarjima qilingan matnga aylantiradi.
 *
 * ⚠️ Ajratib olindi, chunki xato IKKI xil shaklda keladi va ikkalasi
 * ham SHU jadvallardan o'tishi kerak (pastdagi `translatePortalError`
 * izohiga qarang). Ilgari bu mantiq `switch` ichida qulflangan edi va
 * ikkinchi shakl uni butunlay chetlab o'tardi.
 */
function byStatusAndTag(
  status: number | null,
  tag: string | null,
  headers: unknown,
  t: Translate,
): PortalError {
  switch (status) {
    case 400:
      return { message: t('error.badRequest') }
    case 401:
      return {
        message: tag === 'ACCOUNT_CREDENTIALS_INVALID' ? t('error.accountCredentialsInvalid') : t('error.generic'),
      }
    case 404:
      return { message: t('error.zoneNotFound') }
    case 409: {
      // ⚠️ Avval vaucher belgilari, keyin umumiy "qabul qilmayapti":
      // 409 ikki xil sababdan qaytadi va ikkinchisi birinchisini
      // yutib yuborardi.
      // ⚠️ Qurilmada boshqa tashrif ochiq — kartaning holati bilan
      // umuman bog'liq emas, shuning uchun vaucher jadvalidan OLDIN.
      if (tag === 'DEVICE_HAS_OTHER_SESSION') {
        return { message: t('error.deviceHasOtherSession') }
      }
      const key = tag ? VOUCHER_TAG_MESSAGE_KEY[tag] : undefined
      return { message: key ? t(key) : t('error.notAccepting') }
    }
    case 422: {
      const key = tag ? TAG_MESSAGE_KEY[tag] : undefined
      return { message: key ? t(key) : t('error.generic') }
    }
    case 429: {
      const seconds = retryAfterSeconds(headers)
      return {
        message: t('error.tooManyRequests', { seconds: seconds ?? 60 }),
        retryAfterSeconds: seconds,
      }
    }
    case 503:
      return { message: t('error.accessUnavailable') }
    default:
      return { message: t('error.generic') }
  }
}

export function errorStatusAndTag(error: unknown): { status: number | null; tag: string | null } {
  if (error instanceof XsibApiError) return { status: error.code, tag: error.tag }
  if (axios.isAxiosError<ApiErrorBody>(error) && error.response) {
    return { status: error.response.status, tag: error.response.data?.error?.tag ?? null }
  }
  return { status: null, tag: null }
}

/**
 * Backend xatosini mehmonga ko'rsatiladigan, TANLANGAN TILDA matnga
 * aylantiradi.
 *
 * <h2>⚠️ XOM MATN BU YERDAN CHIQMAYDI — IKKI TESHIK YOPILDI</h2>
 *
 * `ErrorResult` javadoc'i shart qo'yadi: ekranda faqat shu funksiya
 * qaytargan, OLDINDAN TARJIMA QILINGAN matn turadi. Funksiyaning o'zi
 * esa ikki yo'ldan xom matnni o'tkazib yuborardi:
 *
 * <ol>
 *   <li><b>{@code XsibApiError}</b> — envelope ichidagi xato
 *       ({@code shared/api/client.ts}). U {@code Error} ning bolasi,
 *       LEKIN {@code AxiosError} EMAS, ya'ni yuqoridagi tekshiruvdan
 *       o'tmasdi va pastdagi "shunchaki {@code message}" tarmog'iga
 *       tushardi. {@code message} esa — serverning xom matni va u
 *       HAR DOIM O'ZBEKCHA ({@code ApiErrorBody} izohi). Ruscha
 *       ochgan mehmon birdan o'zbekcha jumla ko'rardi. Eng achinarlisi:
 *       sinf {@code tag} va {@code code} ni ALLAQACHON olib yuradi —
 *       tarjima uchun kerak bo'lgan hamma narsa qo'lda edi, faqat
 *       o'qilmasdi;</li>
 *   <li><b>boshqa har qanday {@code Error}</b> — masalan Zod sxemasi
 *       yiqilganda. Bu NAZARIY EMAS: 2026-08-08 da aynan shu sodir
 *       bo'lgan va mehmon ekranida internet o'rniga XOM ZOD XATOSI
 *       turgan ({@code features/portal/api.ts} boshidagi izoh). O'shanda
 *       sxema tuzatilgan, lekin TESHIK ochiq qolgan — keyingi shakl
 *       o'zgarishi yana xuddi shu JSON'ni ekranga chiqarardi.</li>
 * </ol>
 *
 * ⚠️ Bu qaror kabinetnikidan ATAYLAB boshqa: {@code selfcare} da
 * server matni ko'rsatiladi (u domen qoidasini yaxshiroq biladi va
 * o'quvchi — shartnomasi bor abonent). Mehmon esa anonim, tilni
 * o'zi tanlagan va texnik matnni umuman o'qiy olmaydi.
 */
export function translatePortalError(error: unknown, t: Translate): PortalError {
  if (error instanceof XsibApiError) {
    return byStatusAndTag(error.code, error.tag, undefined, t)
  }

  if (!axios.isAxiosError<ApiErrorBody>(error)) {
    return { message: t('error.generic') }
  }

  if (!error.response) {
    return { message: t('error.network') }
  }

  const { status, data } = error.response
  return byStatusAndTag(status, data?.error?.tag ?? null, error.response.headers, t)
}
